import React, { useEffect, useContext } from "react"
import styled from "styled-components"
import { em } from "polished"
import {
  CarouselProvider,
  CarouselContext,
  Slider as CarouselSlider,
  Slide as CarouselSlide,
  ButtonBack as CarouselBack,
  ButtonNext as CarouselNext,
} from "pure-react-carousel"
import "pure-react-carousel/dist/react-carousel.es.css"

import { ReactComponent as SvgChevronLeft } from "../../assets/icons/chevron-left.svg"
import { ReactComponent as SvgChevronRight } from "../../assets/icons/chevron-right.svg"

const CarouselContextSubscriber = ({ onChange }) => {
  const carouselContext = useContext(CarouselContext)

  useEffect(() => {
    const cb = () => onChange(carouselContext.state.currentSlide)
    carouselContext.subscribe(cb)
    return () => carouselContext.unsubscribe(cb)
  }, [carouselContext])

  return null
}

const Carousel = ({
  translations,
  slides = [],
  visibleSlides = 1,
  initialSlide = 0,
  insetFocus = false,
  slideGap = 16,
  innerRef,
  providerRef,
  className,
  onChange,
}) => {
  if (!slides?.length) return null

  return (
    <Container ref={innerRef} className={className} $slideGap={slideGap}>
      <CarouselProvider
        ref={providerRef}
        visibleSlides={visibleSlides}
        totalSlides={slides.length}
        currentSlide={initialSlide}
      >
        {onChange && <CarouselContextSubscriber onChange={onChange} />}

        <CarouselSlider>
          {slides.map((slide, i) => (
            <CarouselSlide key={i}>{slide}</CarouselSlide>
          ))}
        </CarouselSlider>

        <PrevNext>
          <CarouselBack
            title={translations.global.previous}
            aria-label={translations.global.previous}
          >
            <SvgChevronLeft aria-label="hidden" />
          </CarouselBack>

          <CarouselNext
            title={translations.global.next}
            aria-label={translations.global.next}
          >
            <SvgChevronRight aria-label="hidden" />
          </CarouselNext>
        </PrevNext>

        {insetFocus && <Focus />}
      </CarouselProvider>
    </Container>
  )
}

export default Carousel

const Focus = styled.div`
  position: absolute;
  z-index: 3;
  top: ${em(10)};
  bottom: ${em(10)};
  left: ${em(10)};
  right: ${em(10)};
  pointer-events: none;
  display: none;
  outline: 1px dotted;
  outline: 4px auto Highlight;
  outline: 4px auto -webkit-focus-ring-color;
`

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  .carousel {
    &,
    &__slider,
    &__slider-tray-wrapper,
    &__slider-tray,
    &__slide,
    &__inner-slide {
      width: 100%;
      height: 100%;
      position: static;
    }

    &,
    &__slider,
    &__slider-tray-wrapper {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
    }

    &__slider {
      width: calc(100% + ${({ $slideGap }) => em($slideGap)});
      position: relative;
      z-index: 1;
      margin: 0 ${({ $slideGap }) => em(-$slideGap / 2)};

      &:focus ~ ${Focus} {
        display: block;
      }
    }

    &__slide {
      height: auto;
      display: flex;
      padding: 0 ${({ $slideGap }) => em($slideGap / 2)};
    }

    &__slider-tray {
      flex-grow: 1;
      display: flex;
      will-change: transform;
    }

    &__inner-slide {
      display: flex;
      position: relative;
    }
  }
`

const PrevNext = styled.div`
  button {
    width: ${em(42)};
    height: ${em(42)};
    padding: ${em(12)};
    position: absolute;
    z-index: 2;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: auto;
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 50%;

    @media ${({ theme }) => theme.mq.largeDown} {
      font-size: ${em(14)};
    }

    &:hover {
      opacity: 0.9;
    }

    &:active {
      opacity: 0.5;
    }

    &[disabled] {
      opacity: 0;
    }

    &.carousel__back-button {
      padding-right: ${em(14)};
      left: 0;
      transform: translate(-50%, -50%);
      box-shadow: -1px 1px 1px rgba(0, 0, 0, 0.2);

      @media ${({ theme }) => theme.mq.xxlargeDown} {
        transform: translate(-25%, -50%);
      }
    }

    &.carousel__next-button {
      padding-left: ${em(14)};
      right: 0;
      transform: translate(50%, -50%);
      box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2);

      @media ${({ theme }) => theme.mq.xxlargeDown} {
        transform: translate(25%, -50%);
      }
    }

    /* expands tap area */
    &::before {
      content: "";
      position: absolute;
      top: ${em(-6)};
      bottom: ${em(-6)};
      left: ${em(-6)};
      right: ${em(-6)};
    }

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }
`
